/**
 * Breadcrumb (≥1024px) and mobile ContextBack (docs/03 §1): breadcrumbs derive
 * from the route tree; the green mobile header carries a chevron + parent name.
 */

import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Fragment, type ReactNode } from 'react';

export interface Crumb {
  readonly label: string;
  readonly href?: string;
}

export function Breadcrumb({ crumbs }: { readonly crumbs: readonly Crumb[] }): ReactNode {
  return (
    <nav
      aria-label="Breadcrumb"
      className="hidden items-center gap-1.5 text-meta text-ink2 md:flex"
    >
      {crumbs.map((crumb, index) => (
        <Fragment key={`${crumb.label}-${String(index)}`}>
          {index > 0 ? <ChevronRight size={14} aria-hidden className="text-ink3" /> : null}
          {crumb.href !== undefined ? (
            <a href={crumb.href} className="text-ink2 no-underline hover:text-ink hover:underline">
              {crumb.label}
            </a>
          ) : (
            <span aria-current="page" className="font-medium text-ink">
              {crumb.label}
            </span>
          )}
        </Fragment>
      ))}
    </nav>
  );
}

export function ContextBack({
  parentLabel,
  title,
  onBack,
  right,
}: {
  readonly parentLabel: string;
  readonly title: string;
  readonly onBack: () => void;
  readonly right?: ReactNode;
}): ReactNode {
  return (
    <header className="flex items-center gap-2 bg-brand-900 px-3 py-2.5 text-on-green md:hidden">
      <button
        type="button"
        onClick={onBack}
        aria-label={`Back to ${parentLabel}`}
        className="flex min-h-11 min-w-11 items-center justify-center rounded-ctl"
      >
        <ChevronLeft size={21} aria-hidden />
      </button>
      <div className="min-w-0">
        <span className="block text-chip text-on-green-sub">{parentLabel}</span>
        <span className="block truncate text-body font-semibold">{title}</span>
      </div>
      <div className="ml-auto">{right}</div>
    </header>
  );
}
